import type { AccessControlProvider, CanParams, CanReturnType } from '@refinedev/core'
import {
  StaffRole,
  Target,
  Action,
  getRolePermissions,
  hasPermission,
} from '@/lib/permissions'

// Maps Refine resource names to permission targets
const resourceToTarget: Record<string, Target> = {
  patients: Target.PATIENT,
  doctors: Target.DOCTOR,
  appointments: Target.APPOINTMENT,
  'lab-results': Target.LAB_RESULT,
  orders: Target.ORDER,
  'quiz-submissions': Target.QUIZ,
  users: Target.USER,
  audit: Target.AUDIT,
  settings: Target.SETTINGS,
  dashboard: Target.DASHBOARD,
  consultations: Target.CONSULTATION,
  products: Target.PRODUCT,
  categories: Target.CATEGORY,
  blog: Target.BLOG,
}

// Maps Refine actions to permission actions
const refineActionToAction: Record<string, Action> = {
  list: Action.READ,
  show: Action.READ,
  create: Action.CREATE,
  edit: Action.UPDATE,
  delete: Action.DELETE,
  clone: Action.CREATE,
}

// Get user role from localStorage
const getUserRole = (): StaffRole | null => {
  try {
    const stored = localStorage.getItem('admin_user_role')
    if (stored && Object.values(StaffRole).includes(stored as StaffRole)) {
      return stored as StaffRole
    }
  } catch {
    // Ignore storage errors
  }

  return null
}

export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action }: CanParams): Promise<CanReturnType> => {
    const role = getUserRole()

    if (!role) {
      return {
        can: false,
        reason: 'User not authenticated',
      }
    }

    // Dashboard is always accessible for authenticated users
    if (resource === 'dashboard') {
      return { can: true }
    }

    // Settings is accessible for own settings
    if (resource === 'settings') {
      return { can: true }
    }

    // My Consultations is only for doctors
    if (resource === 'my-consultations') {
      return {
        can: role === StaffRole.DOCTOR,
        reason: role !== StaffRole.DOCTOR ? 'Only doctors can access this page' : undefined,
      }
    }

    // Calendar is accessible to all staff (shows different data based on role)
    if (resource === 'calendar') {
      return { can: true }
    }

    // Consultations (admin/nurse scheduling) is not for doctors
    if (resource === 'consultations' && role === StaffRole.DOCTOR) {
      return {
        can: false,
        reason: 'Doctors should use My Consultations page',
      }
    }

    // Get permission target and action
    const target = resourceToTarget[resource || '']
    const permAction = refineActionToAction[action || '']

    if (!target || !permAction) {
      // Allow unknown resources by default (be permissive)
      return { can: true }
    }

    // Check if user has permission
    const userPermissions = getRolePermissions(role)
    if (hasPermission(userPermissions, target, permAction)) {
      return { can: true }
    }

    return {
      can: false,
      reason: `You don't have permission to ${action} ${resource}`,
    }
  },

  options: {
    buttons: {
      enableAccessControl: true,
      hideIfUnauthorized: true,
    },
  },
}

// Helper to set user role in localStorage
export const setUserRole = (role: StaffRole) => {
  localStorage.setItem('admin_user_role', role)
}

// Helper to clear user role from localStorage
export const clearUserRole = () => {
  localStorage.removeItem('admin_user_role')
}
